/**
 * Enquiry.jsx  — Enquiry list page
 *
 * Changes vs original:
 *  - Uses the new paginated API (page + limit params) so it never fetches
 *    the entire collection at once.
 *  - Resolves customer / vendor names from a lightweight lookup endpoint
 *    (only fetches names, not full records) — avoids loading all master data
 *    just to render a table column.
 *  - Search and status filters are applied server-side; the frontend just
 *    sends them as query params and re-fetches.
 *  - Debounced search input prevents a request on every keystroke.
 *  - All working conditions (selection, bulk delete, navigation) unchanged.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URLS } from '../config';
import { apiRequest } from '../utils/apiRequest';
import formatDate from '../utils/formatDate';
import AccessDenied from '../Components/AccessDenied';
import {
  FiEdit, FiPlus, FiEye, FiTrash2,
  FiChevronUp, FiChevronDown, FiRefreshCw, FiDownload, FiX,
} from 'react-icons/fi';
import { FaFileInvoiceDollar, FaFolderOpen } from 'react-icons/fa';
import { FaMoneyCheckDollar } from 'react-icons/fa6';
import { BsJournalBookmarkFill } from "react-icons/bs";
import { IoNewspaperSharp, IoWarningOutline } from 'react-icons/io5';
import { useToast } from '../Components/Toast';
import Select from 'react-select';
import FilterPanel, { FilterField, filterSelectStyles } from '../Components/FilterPanel';

const PAGE_LIMIT = 25;

// Optional Manday-form fields the user can toggle on/off as extra table columns.
// "company" is only resolvable for global_superadmin (company list is admin-only).
const COLUMN_OPTIONS = [
  { key: 'company', label: 'Company', adminOnly: true },
  { key: 'vendorName', label: 'Vendor Name' },
  { key: 'country', label: 'Country' },
  { key: 'totalDays', label: 'Total Days' },
  { key: 'coordinator', label: 'Customer Coordinator' },
  { key: 'products', label: 'Products' },
  { key: 'inspectors', label: 'Inspector' },
  { key: 'enquiryType', label: 'Enquiry Type' },
];

const Enquiry = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const toast = useToast();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_LIMIT);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [customerLookup, setCustomerLookup] = useState({});
  const [vendorLocationLookup, setVendorLocationLookup] = useState({});
  const [vendorNameLookup, setVendorNameLookup] = useState({});
  const [productLookup, setProductLookup] = useState({});
  const [lookupsLoading, setLookupsLoading] = useState(true);

  // Column-visibility settings (which optional Manday fields show as table columns)
  const [visibleCols, setVisibleCols] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('enquiry_filters') || '{}').visibleCols || {}; } catch { return {}; }
  });
  const toggleColumn = (key) => setVisibleCols(prev => ({ ...prev, [key]: !prev[key] }));

  const [statusCounts, setStatusCounts] = useState({ NEW: 0, INPROCESS: 0, WON: 0, LOST: 0 });
  const [countsLoading, setCountsLoading] = useState(false);

  // Restore filters from sessionStorage so browser-back preserves them
  const [_sf] = useState(() => { try { return JSON.parse(sessionStorage.getItem('enquiry_filters') || '{}'); } catch { return {}; } });

    // Send-to-Finance status for the currently selected enquiry (single-select only)
  const [financeStatus, setFinanceStatus] = useState({ sent: false, invoiceExists: false, hasNewWork: false });
  const [sendingToFinance, setSendingToFinance] = useState(false);

  // Send-to-Finance popup: pick which inspection dates to bill
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendInspectorDates, setSendInspectorDates] = useState([]);   // report-status inspectorDates rows
  const [pickedDates, setPickedDates] = useState({});                 // row index -> array of picked dates ('*' = whole legacy row)

  // Filter state — changes trigger a fresh fetch
  const [search, setSearch] = useState(_sf.search || '');
  const [status, setStatus] = useState(_sf.status || '');
  const [sortDir, setSortDir] = useState(_sf.sortDir || '');
  const [enquiryDateSortDir, setEnquiryDateSortDir] = useState(_sf.enquiryDateSortDir || '');
  const [showExportPanel, setShowExportPanel] = useState(false);
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');
  const [filterCompany, setFilterCompany] = useState(_sf.filterCompany || '');
  const [filterCoordinator, setFilterCoordinator] = useState(_sf.filterCoordinator || '');
  const [filterMonth, setFilterMonth] = useState(_sf.filterMonth || '');
  const [filterInspectionMonth, setFilterInspectionMonth] = useState(_sf.filterInspectionMonth || '');
  const [filterEnquiryType, setFilterEnquiryType] = useState(_sf.filterEnquiryType || '');
  const [companies, setCompanies] = useState([]);
  const [coordinators, setCoordinators] = useState([]);
  const searchRef = useRef(_sf.search || '');
  const sortDirRef = useRef(_sf.sortDir || '');
  const enquiryDateSortDirRef = useRef(_sf.enquiryDateSortDir || '');
  const filterCompanyRef = useRef(_sf.filterCompany || '');
  const filterCoordinatorRef = useRef(_sf.filterCoordinator || '');
  const filterMonthRef = useRef(_sf.filterMonth || '');
  const filterInspectionMonthRef = useRef(_sf.filterInspectionMonth || '');
  const filterEnquiryTypeRef = useRef(_sf.filterEnquiryType || '');
  const debounceId = useRef(null);

  useEffect(() => {
    sessionStorage.setItem('enquiry_filters', JSON.stringify({
      search, status, sortDir, enquiryDateSortDir,
      filterCompany, filterCoordinator, filterMonth, filterInspectionMonth, filterEnquiryType, visibleCols,
    }));
  }, [search, status, sortDir, enquiryDateSortDir, filterCompany, filterCoordinator, filterMonth, filterInspectionMonth, filterEnquiryType, visibleCols]);

  /* ── Fetch ────────────────────────────────────────────────────────── */
  const fetchStatusCounts = useCallback(async (
    srch = searchRef.current,
    fc   = filterCompanyRef.current,
    fco  = filterCoordinatorRef.current,
    fm   = filterMonthRef.current,
    fim  = filterInspectionMonthRef.current,
    fet  = filterEnquiryTypeRef.current,
  ) => {
    if (!token) return;
    setCountsLoading(true);
    try {
      const statuses = ['NEW', 'INPROCESS', 'WON', 'LOST'];
      const results = await Promise.all(
        statuses.map(s => {
          const params = new URLSearchParams({ page: 1, limit: 1, status: s });
          if (srch) params.append('search', srch);
          if (fc)   params.append('company', fc);
          if (fco)  params.append('coordinator', fco);
          if (fm)   params.append('month', fm);
          if (fim)  params.append('inspectionMonth', fim);
          if (fet)  params.append('enquiryType', fet);
          return apiRequest(`${API_URLS.ENQUIRIES}?${params}`, {}, token);
        })
      );
      const newCounts = {};
      statuses.forEach((s, i) => {
        newCounts[s] = results[i]?.ok ? (results[i].data.total || 0) : 0;
      });
      setStatusCounts(newCounts);
    } catch (error) {
      console.error("Error fetching status counts:", error);
    } finally {
      setCountsLoading(false);
    }
  }, [token]);

  const fetchRows = useCallback(async (
    p = 1,
    srch = searchRef.current,
    st = status,
    sd = sortDirRef.current,
    fc = filterCompanyRef.current,
    fco = filterCoordinatorRef.current,
    eds = enquiryDateSortDirRef.current,
    fm = filterMonthRef.current,
    fim = filterInspectionMonthRef.current,
    fet = filterEnquiryTypeRef.current,
  ) => {
    setLoading(true);
    const params = new URLSearchParams({ page: p, limit });
    if (srch) params.append('search', srch);
    if (st) params.append('status', st);
    if (sd) { params.append('sortBy', 'inspectionDate'); params.append('sortDir', sd); }
    else if (eds) { params.append('sortBy', 'enquiryDate'); params.append('sortDir', eds); }
    if (fc) params.append('company', fc);
    if (fco) params.append('coordinator', fco);
    if (fm) params.append('month', fm);
    if (fim) params.append('inspectionMonth', fim);
    if (fet) params.append('enquiryType', fet);

    const result = await apiRequest(`${API_URLS.ENQUIRIES}?${params}`, {}, token);
    if (!result) return;
    if (result.denied) { setDenied(result); setLoading(false); return; }
    if (result.ok) {
      setRows(result.data.rows || []);
      setTotal(result.data.total || 0);
      setPage(result.data.page || p);
      setPages(result.data.pages || 1);
      setSelectedIds([]);
    }
    setLoading(false);
  }, [token, status, limit]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchRows(1, search, status); }, [status]);
  useEffect(() => {
    fetchRows(1);
    fetchStatusCounts();
  }, [token, fetchStatusCounts, fetchRows]);

  useEffect(() => {
    const fetchLookups = async () => {
      if (!token) return;
      setLookupsLoading(true);
      const isAdmin = user?.role === 'global_superadmin';
      const canSeeCoordinatorFilter = user?.role !== 'coordinator';
      const [custRes, vendRes, prodRes, compRes, usersRes] = await Promise.all([
        apiRequest(`${API_URLS.CUSTOMERS}?limit=1000`, {}, token),
        apiRequest(`${API_URLS.VENDORS}?limit=1000`, {}, token),
        apiRequest(`${API_URLS.PRODUCTS}?limit=1000`, {}, token),
        isAdmin ? apiRequest(`${API_URLS.COMPANIES}`, {}, token) : Promise.resolve(null),
        canSeeCoordinatorFilter ? apiRequest(`${API_URLS.USERS}?limit=1000`, {}, token) : Promise.resolve(null),
      ]);
      if (custRes?.ok) {
        const map = {};
        (custRes.data.rows || []).forEach(c => {
          map[c.id || c._id] = c.customerName || c.companyName;
        });
        setCustomerLookup(map);
      }
      if (vendRes?.ok) {
        const nameMap = {};
        const locMap = {};
        (vendRes.data.rows || []).forEach(v => {
          const id = v.id || v._id;
          nameMap[id] = v.vendorName || v.companyName;
          const parts = [v.city, v.country].filter(Boolean);
          locMap[id] = parts.join(', ') || '—';
        });
        setVendorNameLookup(nameMap);
        setVendorLocationLookup(locMap);
      }
      if (prodRes?.ok) {
        const map = {};
        (prodRes.data.rows || []).forEach(p => { map[p.id || p._id] = p.name; });
        setProductLookup(map);
      }
      if (compRes?.ok) {
        setCompanies(compRes.data || []);
      }
      if (usersRes?.ok) {
        const sorted = (usersRes.data.rows || [])
          .filter(u => u.employeeType === 'coordinator')
          .sort((a, b) => (a.employeeName || '').localeCompare(b.employeeName || ''));
        setCoordinators(sorted);
      }
      setLookupsLoading(false);
    };
    fetchLookups();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user?.role]);

  // Debounce search input — wait 400 ms before firing
  const handleSearchChange = (val) => {
    setSearch(val);
    searchRef.current = val;
    clearTimeout(debounceId.current);
    debounceId.current = setTimeout(() => {
      fetchRows(1, val, status);
      fetchStatusCounts(val, filterCompanyRef.current, filterCoordinatorRef.current);
    }, 400);
  };

  const handleFilterCompanyChange = (val) => {
    setFilterCompany(val);
    filterCompanyRef.current = val;
    fetchRows(1, searchRef.current, status, sortDirRef.current, val, filterCoordinatorRef.current);
    fetchStatusCounts(searchRef.current, val, filterCoordinatorRef.current);
  };

  const handleFilterCoordinatorChange = (val) => {
    setFilterCoordinator(val);
    filterCoordinatorRef.current = val;
    fetchRows(1, searchRef.current, status, sortDirRef.current, filterCompanyRef.current, val, enquiryDateSortDirRef.current, filterMonthRef.current, filterInspectionMonthRef.current);
    fetchStatusCounts(searchRef.current, filterCompanyRef.current, val, filterMonthRef.current, filterInspectionMonthRef.current);
  };

  const handleFilterMonthChange = (val) => {
    setFilterMonth(val);
    filterMonthRef.current = val;
    fetchRows(1, searchRef.current, status, sortDirRef.current, filterCompanyRef.current, filterCoordinatorRef.current, enquiryDateSortDirRef.current, val, filterInspectionMonthRef.current);
    fetchStatusCounts(searchRef.current, filterCompanyRef.current, filterCoordinatorRef.current, val, filterInspectionMonthRef.current);
  };

  const handleFilterInspectionMonthChange = (val) => {
    setFilterInspectionMonth(val);
    filterInspectionMonthRef.current = val;
    fetchRows(1, searchRef.current, status, sortDirRef.current, filterCompanyRef.current, filterCoordinatorRef.current, enquiryDateSortDirRef.current, filterMonthRef.current, val, filterEnquiryTypeRef.current);
    fetchStatusCounts(searchRef.current, filterCompanyRef.current, filterCoordinatorRef.current, filterMonthRef.current, val, filterEnquiryTypeRef.current);
  };

  const handleFilterEnquiryTypeChange = (val) => {
    setFilterEnquiryType(val);
    filterEnquiryTypeRef.current = val;
    fetchRows(1, searchRef.current, status, sortDirRef.current, filterCompanyRef.current, filterCoordinatorRef.current, enquiryDateSortDirRef.current, filterMonthRef.current, filterInspectionMonthRef.current, val);
    fetchStatusCounts(searchRef.current, filterCompanyRef.current, filterCoordinatorRef.current, filterMonthRef.current, filterInspectionMonthRef.current, val);
  };

  // Options for the filter panel's dropdowns (react-select { value, label } shape)
  const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const monthOptions = MONTH_NAMES.map((m, i) => ({ value: String(i + 1), label: m }));
  const companyOptions = companies.map(c => ({ value: String(c.id || c._id), label: c.companyName }));
  const coordinatorOptions = coordinators.map(u => ({ value: String(u.id), label: u.employeeName }));
  const enquiryTypeOptions = [{ value: 'MANDAY', label: 'Manday' }, { value: 'MANMONTH', label: 'Manmonth' }];
  const pickOption = (options, value) => options.find(o => o.value === String(value)) || null;

  // FilterPanel hands back every value; route the one that changed to its handler
  // (each handler refetches with the right arguments).
  const handleFilterPanelChange = (v) => {
    if (v.company !== filterCompany) handleFilterCompanyChange(v.company);
    else if (v.coordinator !== filterCoordinator) handleFilterCoordinatorChange(v.coordinator);
    else if (v.month !== filterMonth) handleFilterMonthChange(v.month);
    else if (v.inspectionMonth !== filterInspectionMonth) handleFilterInspectionMonthChange(v.inspectionMonth);
    else if (v.enquiryType !== filterEnquiryType) handleFilterEnquiryTypeChange(v.enquiryType);
  };

  const clearAdvancedFilters = () => {
    setFilterCompany(''); filterCompanyRef.current = '';
    setFilterCoordinator(''); filterCoordinatorRef.current = '';
    setFilterMonth(''); filterMonthRef.current = '';
    setFilterInspectionMonth(''); filterInspectionMonthRef.current = '';
    setFilterEnquiryType(''); filterEnquiryTypeRef.current = '';
    fetchRows(1, searchRef.current, status, sortDirRef.current, '', '', enquiryDateSortDirRef.current, '', '', '');
    fetchStatusCounts(searchRef.current, '', '', '', '', '');
  };

  /* ── Selection ───────────────────────────────────────────────────── */
  const toggleRow = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const toggleAll = () =>
    setSelectedIds(rows.length > 0 && selectedIds.length === rows.length
      ? [] : rows.map(r => r._id));

  /* ── Send to Finance (single selected row) ──────────────────────── */
  const selectedRow = selectedIds.length === 1 ? rows.find(r => r._id === selectedIds[0]) : null;
  const hasUploadedReport = !!selectedRow?.deliverables?.some(
    d => d.fileUrl || (Array.isArray(d.fileUrls) && d.fileUrls.length > 0)
  );

  const fetchFinanceStatus = useCallback(async () => {
    if (selectedIds.length !== 1) { setFinanceStatus({ sent: false, invoiceExists: false, hasNewWork: false }); return; }
    const res = await apiRequest(`${API_URLS.FINANCE_NOTIFICATIONS}/report-status/${selectedIds[0]}`, {}, token);
    if (res?.ok) {
      setFinanceStatus({
        sent: !!res.data.sentToFinance,
        invoiceExists: !!res.data.invoiceExists,
        hasNewWork: !!res.data.hasNewWork,
      });
    }
  }, [selectedIds, token]);

  useEffect(() => {
    if (selectedIds.length !== 1) { setFinanceStatus({ sent: false, invoiceExists: false, hasNewWork: false }); return; }
    // Seed immediately from the row data already in the table (no flash of an enabled button)
    setFinanceStatus(prev => ({ ...prev, invoiceExists: !!selectedRow?.financeInvoice }));
    fetchFinanceStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, token]);

  const doSendToFinance = async (selectedDates) => {
    setSendingToFinance(true);
    try {
      const body = { enquiryId: selectedIds[0] };
      if (Array.isArray(selectedDates) && selectedDates.length > 0) body.selectedDates = selectedDates;
      const res = await apiRequest(
        `${API_URLS.FINANCE_NOTIFICATIONS}/report-uploaded`,
        { method: 'POST', body },
        token
      );
      if (res?.ok) {
        toast.success('Enquiry sent to Finance for invoicing.');
        setShowSendModal(false);
      } else if (res?.status === 409 || res?.data?.invoiceExists) {
        toast.info(res?.data?.message || 'Finance has already created an invoice for this enquiry.');
        setShowSendModal(false);
      } else {
        toast.info(res?.data?.message || 'Could not send to Finance.');
      }
      await fetchFinanceStatus();
    } finally {
      setSendingToFinance(false);
    }
  };

  // Opens the date-selection popup (falls back to a direct send when there are no date lists)
  const handleSendToFinance = async () => {
    if (selectedIds.length !== 1 || sendingToFinance) return;
    const res = await apiRequest(`${API_URLS.FINANCE_NOTIFICATIONS}/report-status/${selectedIds[0]}`, {}, token);
    if (!res?.ok) { toast.info(res?.data?.message || 'Could not load inspection dates.'); return; }
    const list = res.data.inspectorDates || [];
    if (list.length === 0) { await doSendToFinance([]); return; }

    // Pre-tick everything that can be billed: report-uploaded dates not yet invoiced,
    // and legacy manual-days rows that are not invoiced yet
    const picked = {};
    list.forEach((ins, idx) => {
      if ((ins.dates || []).length === 0) {
        picked[idx] = !ins.hasInvoice && (ins.reportedDates || []).length > 0 ? ['*'] : [];
      } else {
        const reported = new Set(ins.reportedDates || []);
        const invoiced = new Set(ins.invoicedDates || []);
        picked[idx] = (ins.dates || []).filter(d => reported.has(d) && !invoiced.has(d));
      }
    });
    setSendInspectorDates(list);
    setPickedDates(picked);
    setShowSendModal(true);
  };

  const toggleSendDate = (idx, date) =>
    setPickedDates(prev => {
      const cur = prev[idx] || [];
      return { ...prev, [idx]: cur.includes(date) ? cur.filter(d => d !== date) : [...cur, date] };
    });

  const pickedCount = Object.values(pickedDates).reduce((n, arr) => n + (arr?.length || 0), 0);

  const confirmSendToFinance = () => {
    const payload = sendInspectorDates
      .map((ins, idx) => {
        const picked = pickedDates[idx] || [];
        if (picked.length === 0) return null;
        const isLegacy = (ins.dates || []).length === 0;
        return { inspectorId: ins.inspectorId, inspectorName: ins.inspectorName, dates: isLegacy ? [] : picked };
      })
      .filter(Boolean);
    if (payload.length === 0) { toast.info('Select at least one inspection date.'); return; }
    doSendToFinance(payload);
  };

  /* ── Sort ───────────────────────────────────────────────────────── */
  const toggleInspectionDateSort = () => {
    const next = sortDir === 'asc' ? 'desc' : 'asc';
    setSortDir(next);
    sortDirRef.current = next;
    setEnquiryDateSortDir('');
    enquiryDateSortDirRef.current = '';
    fetchRows(1, searchRef.current, status, next, filterCompanyRef.current, filterCoordinatorRef.current, '');
  };

  const toggleEnquiryDateSort = () => {
    const next = enquiryDateSortDir === 'asc' ? 'desc' : 'asc';
    setEnquiryDateSortDir(next);
    enquiryDateSortDirRef.current = next;
    setSortDir('');
    sortDirRef.current = '';
    fetchRows(1, searchRef.current, status, '', filterCompanyRef.current, filterCoordinatorRef.current, next);
  };

  /* ── Delete ──────────────────────────────────────────────────────── */
  const handleDelete = () => {
    if (!selectedIds.length) return;
    toast.deleteConfirm(`Delete ${selectedIds.length} selected enquiries?`, async () => {
      setLoading(true);
      let ok = 0;
      for (const id of selectedIds) {
        const res = await apiRequest(`${API_URLS.ENQUIRIES}/${id}`, { method: 'DELETE' }, token);
        if (res?.ok) ok++;
      }
      setLoading(false);
      if (ok > 0) {
        toast.success(`Deleted ${ok} items.`);
        fetchRows(page);
        fetchStatusCounts();
      } else toast.error('Failed to delete items.');
    });
  };

  /* ── Export ──────────────────────────────────────────────────────── */
  const handleExport = async (fromDate = '', toDate = '') => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);
    if (filterCompany) params.append('company', filterCompany);
    if (filterCoordinator) params.append('coordinator', filterCoordinator);
    const res = await fetch(`${API_URLS.ENQUIRIES}/export?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const suffix = fromDate && toDate ? `${fromDate}_to_${toDate}` : 'All';
      a.download = `EnquiryList_${suffix}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      toast.error('Export failed. Please try again.');
    }
  };

  const canExport = ['global_superadmin', 'company_admin', 'admin'].includes(user?.role);
  const canSeeCoordinator = ['global_superadmin', 'company_admin', 'admin', 'hod', 'project_manager'].includes(user?.role);
  const isAdmin = user?.role === 'global_superadmin';
  // Coordinator filter (in the advanced filters panel): every role except a plain coordinator can see it
  const canSeeCoordinatorFilter = user?.role !== 'coordinator';

  const companyLookup = useMemo(
    () => Object.fromEntries(companies.map(c => [c.id || c._id, c.companyName])),
    [companies]
  );
  const visibleColumnOptions = COLUMN_OPTIONS.filter(opt => !opt.adminOnly || isAdmin);
  const visibleColCount = visibleColumnOptions.filter(opt => visibleCols[opt.key]).length;

  /* ── Navigation ──────────────────────────────────────────────────── */
  // Manday.jsx handles both Enquiry Types (Manday/Manmonth) as one page — Enquiry
  // Type is just a field on the form, not a separate route.
  const openAdd = () => navigate('/enquiry/manday/new');
  const openEdit = () => selectedIds.length === 1 && navigate(`/enquiry/manday/${selectedIds[0]}`);
  const openView = () => selectedIds.length === 1 && navigate(`/enquiry/manday/${selectedIds[0]}?view=true`);

  if (denied) return (
    <div className="vendor-page">
      <div className="management-card">
        <AccessDenied message={denied.message} required={denied.required} />
      </div>
    </div>
  );

  return (
    <div className="vendor-page">

      {/* ── Action bar ── */}
      <div className="vendor-action-bar">
        <button className="new-user-btn" onClick={openAdd}><FiPlus /> New Enquiry</button>
        <div style={{ width: 1, height: 24, background: '#ccc', margin: '0 8px' }} />
        <button style={{ padding : "0 10px" }} className="action-btn view-btn" disabled={selectedIds.length !== 1} onClick={openView}><FiEye /> View</button>
        <button style={{ padding : "0 10px" }} className="action-btn edit-btn" disabled={selectedIds.length !== 1} onClick={openEdit}><FiEdit /> Edit</button>
        <button
          style={{
            padding: '0 10px',
            background: financeStatus.invoiceExists && !financeStatus.hasNewWork ? '#95a5a6'
              : (financeStatus.sent || financeStatus.hasNewWork) ? '#e67e22' : undefined,
            color: (financeStatus.invoiceExists || financeStatus.sent) ? '#fff' : undefined,
            borderColor: financeStatus.invoiceExists && !financeStatus.hasNewWork ? '#95a5a6'
              : (financeStatus.sent || financeStatus.hasNewWork) ? '#e67e22' : undefined,
          }}
          className="action-btn"
          disabled={selectedIds.length !== 1 || !hasUploadedReport || (financeStatus.invoiceExists && !financeStatus.hasNewWork) || sendingToFinance}
          title={
            selectedIds.length !== 1 ? 'Select exactly one enquiry'
            : !hasUploadedReport ? 'Upload at least one report/deliverable file first'
            : financeStatus.invoiceExists && financeStatus.hasNewWork ? 'New inspection dates have been added since the last invoice'
            : financeStatus.invoiceExists ? 'Finance has already created an invoice for this enquiry'
            : financeStatus.sent ? 'Already sent — sending again updates the same finance entry'
            : 'Notify Finance that reports are ready for invoicing'
          }
          onClick={handleSendToFinance}
        >
          <FaFileInvoiceDollar />
          {sendingToFinance ? 'Sending…'
            : financeStatus.invoiceExists && financeStatus.hasNewWork ? 'Send New Dates to Finance'
            : financeStatus.invoiceExists ? 'Invoice Created'
            : financeStatus.sent ? 'Re-send to Finance'
            : 'Send to Finance'}
        </button>
        {user?.role === 'global_superadmin' && (
          <button style={{ padding: "0 10px" }} className="action-btn delete-btn" disabled={!selectedIds.length} onClick={handleDelete}><FiTrash2 /> <span className="desktop-only">Delete</span></button>
        )}
        <button style={{ padding : "0 10px" }} className="action-btn export-btn" onClick={() => { fetchRows(page); fetchStatusCounts(); }}><FiRefreshCw size={13} /> <span className="desktop-only">Refresh</span></button>
        {canExport && (
          <button
            className="action-btn export-btn"
            onClick={() => setShowExportPanel(v => !v)}
            style={{ background: showExportPanel ? '#155222' : '#1a6b2a', color: '#fff', borderColor: '#1a6b2a', padding : "0 10px" }}
          >
            <FiDownload size={13} /> <span className="desktop-only">Export</span>
          </button>
        )}

        {/* Filters */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search enquiries…"
            value={search}
            onChange={e => handleSearchChange(e.target.value)}
            className="filter-panel-search"
          />
          <FilterPanel
            fullWidth
            title="Filter enquiries"
            values={{ company: filterCompany, coordinator: filterCoordinator, month: filterMonth, inspectionMonth: filterInspectionMonth, enquiryType: filterEnquiryType }}
            emptyValues={{ company: '', coordinator: '', month: '', inspectionMonth: '', enquiryType: '' }}
            onChange={handleFilterPanelChange}
            onClear={clearAdvancedFilters}
            extra={
              <div className="filter-panel-section">
                <div className="filter-panel-title">
                  <span>Show columns</span>
                  {visibleColCount > 0 && (
                    <button type="button" className="filter-panel-section-action" onClick={() => setVisibleCols({})}>
                      Reset columns
                    </button>
                  )}
                </div>
                <div className="filter-panel-checks">
                  {visibleColumnOptions.map(opt => (
                    <label key={opt.key} className="filter-panel-check">
                      <input type="checkbox" checked={!!visibleCols[opt.key]} onChange={() => toggleColumn(opt.key)} />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>
            }
          >
            {(filters, set) => (
              <>
                {isAdmin && (
                  <FilterField label="Company">
                    <Select
                      options={companyOptions}
                      value={pickOption(companyOptions, filters.company)}
                      onChange={o => set({ company: o ? o.value : '' })}
                      placeholder="All Companies"
                      isClearable
                      styles={filterSelectStyles}
                    />
                  </FilterField>
                )}
                {canSeeCoordinatorFilter && (
                  <FilterField label="Coordinator">
                    <Select
                      options={coordinatorOptions}
                      value={pickOption(coordinatorOptions, filters.coordinator)}
                      onChange={o => set({ coordinator: o ? o.value : '' })}
                      placeholder="All Coordinators"
                      isClearable
                      styles={filterSelectStyles}
                    />
                  </FilterField>
                )}
                <FilterField label="Enquiry month">
                  <Select
                    options={monthOptions}
                    value={pickOption(monthOptions, filters.month)}
                    onChange={o => set({ month: o ? o.value : '' })}
                    placeholder="All Months"
                    isClearable
                    styles={filterSelectStyles}
                  />
                </FilterField>
                <FilterField label="Inspection month">
                  <Select
                    options={monthOptions}
                    value={pickOption(monthOptions, filters.inspectionMonth)}
                    onChange={o => set({ inspectionMonth: o ? o.value : '' })}
                    placeholder="All Months"
                    isClearable
                    styles={filterSelectStyles}
                  />
                </FilterField>
                <FilterField label="Enquiry type">
                  <Select
                    options={enquiryTypeOptions}
                    value={pickOption(enquiryTypeOptions, filters.enquiryType)}
                    onChange={o => set({ enquiryType: o ? o.value : '' })}
                    placeholder="All Types"
                    isClearable
                    styles={filterSelectStyles}
                  />
                </FilterField>
              </>
            )}
          </FilterPanel>
        </div>
      </div>

      {/* ── Export panel ── */}
      {canExport && showExportPanel && (
        <div style={{
          display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
          padding: '10px 16px', background: '#f0f4fa', borderBottom: '1px solid #ccc',
        }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#444' }}>From</label>
          <input
            type="date"
            value={exportFromDate}
            onChange={e => setExportFromDate(e.target.value)}
            style={{ padding: '5px 8px', border: '1px solid #ccc', borderRadius: 0, fontSize: 12 }}
          />
          <label style={{ fontSize: 12, fontWeight: 600, color: '#444' }}>To</label>
          <input
            type="date"
            value={exportToDate}
            onChange={e => setExportToDate(e.target.value)}
            style={{ padding: '5px 8px', border: '1px solid #ccc', borderRadius: 0, fontSize: 12 }}
          />
          <button
            onClick={() => { handleExport(exportFromDate, exportToDate); setShowExportPanel(false); }}
            disabled={!exportFromDate || !exportToDate}
            style={{
              padding: '5px 14px', background: '#1a6b2a', color: '#fff',
              border: '1px solid #1a6b2a', borderRadius: 0, fontSize: 12,
              cursor: (!exportFromDate || !exportToDate) ? 'not-allowed' : 'pointer',
              opacity: (!exportFromDate || !exportToDate) ? 0.5 : 1,
              display: 'inline-flex', alignItems: 'center', gap: 5,
            }}
          >
            <FiDownload size={12} /> Export by Date Range
          </button>
          <button
            onClick={() => { handleExport('', ''); setShowExportPanel(false); }}
            style={{
              padding: '5px 14px', background: '#1a3a6b', color: '#fff',
              border: '1px solid #1a3a6b', borderRadius: 0, fontSize: 12, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 5,
            }}
          >
            <FiDownload size={12} /> Export All
          </button>
          <button
            onClick={() => setShowExportPanel(false)}
            style={{
              padding: '5px 10px', background: '#fff', color: '#888',
              border: '1px solid #ccc', borderRadius: 4, fontSize: 12, cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* ── Table card ── */}
      <div className="management-card">
        <div className="management-header" onClick={() => setIsCollapsed(v => !v)}>
          <div className="header-title">
            <BsJournalBookmarkFill size={18} />
            <h2 onClick={() => setStatus('')} style={{ cursor: "pointer" }}>Enquiry List</h2>
            <span className="vendor-badge">{total}</span>
          </div>
          <div>{isCollapsed ? <FiChevronDown /> : <FiChevronUp />}</div>
        </div>

        <div className='enquiry-status-wrapper'>
          <button
            className='enquiry-status-btn'
            onClick={() => setStatus(status === 'NEW' ? '' : 'NEW')}
            style={{
              fontWeight: status === 'NEW' ? "600" : "normal",
              transform: status === 'NEW' ? "translateY(-3px)" : "translateY(0)",
              boxShadow: status === 'NEW' ? "0 6px 16px rgba(0,0,0,0.35)" : "none",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
            }}
          >
            NEW
            <span style={{ color: "#fff" }} className='status-count status-new'>
              {countsLoading ? '...' : statusCounts.NEW}
            </span>
          </button>

          <button
            className='enquiry-status-btn'
            onClick={() => setStatus(status === 'INPROCESS' ? '' : 'INPROCESS')}
            style={{
              fontWeight: status === 'INPROCESS' ? "600" : "normal",
              transform: status === 'INPROCESS' ? "translateY(-3px)" : "translateY(0)",
              boxShadow: status === 'INPROCESS' ? "0 6px 16px rgba(0,0,0,0.35)" : "none",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
            }}
          >
            IN PROCESS
            <span style={{ color: "#fff" }} className='status-count status-inprocess'>
              {countsLoading ? '...' : statusCounts.INPROCESS}
            </span>
          </button>

          <button
            className='enquiry-status-btn'
            onClick={() => setStatus(status === 'WON' ? '' : 'WON')}
            style={{
              fontWeight: status === 'WON' ? "600" : "normal",
              transform: status === 'WON' ? "translateY(-3px)" : "translateY(0)",
              boxShadow: status === 'WON' ? "0 6px 16px rgba(0,0,0,0.35)" : "none",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
            }}
          >
            WON
            <span style={{ color: "#fff" }} className='status-count status-won'>
              {countsLoading ? '...' : statusCounts.WON}
            </span>
          </button>

          <button
            className='enquiry-status-btn'
            onClick={() => setStatus(status === 'LOST' ? '' : 'LOST')}
            style={{
              fontWeight: status === 'LOST' ? "600" : "normal",
              transform: status === 'LOST' ? "translateY(-3px)" : "translateY(0)",
              boxShadow: status === 'LOST' ? "0 6px 16px rgba(0,0,0,0.35)" : "none",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
            }}
          >
            LOST
            <span style={{ color: "#fff" }} className='status-count status-lost'>
              {countsLoading ? '...' : statusCounts.LOST}
            </span>
          </button>
        </div>

        {!isCollapsed && (
          <div className="vendor-table-container">
            {(loading || lookupsLoading) && rows.length === 0
              ? <div style={{ padding: 20 }}>Loading…</div>
              : (
                <>
                  <table className="vendor-table">
                    <thead>
                      <tr>
                        <th style={{ width: 40 }}>
                          <input type="checkbox"
                            checked={rows.length > 0 && selectedIds.length === rows.length}
                            onChange={toggleAll} />
                        </th>
                        <th>JOB NUMBER</th>
                        <th
                          onClick={toggleEnquiryDateSort}
                          style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                        >
                          ENQUIRY DATE{' '}
                          {enquiryDateSortDir === 'asc' ? <FiChevronUp size={13} /> : enquiryDateSortDir === 'desc' ? <FiChevronDown size={13} /> : <span style={{ color: '#bbb' }}><FiChevronUp size={11} /></span>}
                        </th>
                        <th className="enquiry-table-mobile-hide">CUSTOMER NAME</th>
                        <th
                          className="enquiry-table-mobile-hide"
                          onClick={toggleInspectionDateSort}
                          style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                        >
                          INSPECTION DATE{' '}
                          {sortDir === 'asc' ? <FiChevronUp size={13} /> : sortDir === 'desc' ? <FiChevronDown size={13} /> : <span style={{ color: '#bbb' }}><FiChevronUp size={11} /></span>}
                        </th>
                        <th className="enquiry-table-mobile-hide">VENDOR LOCATION</th>
                        <th className="enquiry-table-mobile-hide">CUSTOMER REQUIREMENT</th>
                        <th className="enquiry-table-mobile-hide">REMARK STATUS</th>
                        <th className="enquiry-table-mobile-hide">REMARK</th>
                        {canSeeCoordinator && <th className="enquiry-table-mobile-hide">COORDINATOR</th>}
                        {visibleCols.company && <th className="enquiry-table-mobile-hide">COMPANY</th>}
                        {visibleCols.vendorName && <th className="enquiry-table-mobile-hide">VENDOR NAME</th>}
                        {visibleCols.country && <th className="enquiry-table-mobile-hide">COUNTRY</th>}
                        {visibleCols.totalDays && <th className="enquiry-table-mobile-hide">TOTAL DAYS</th>}
                        {visibleCols.coordinator && <th className="enquiry-table-mobile-hide">CUSTOMER COORDINATOR</th>}
                        {visibleCols.products && <th className="enquiry-table-mobile-hide">PRODUCTS</th>}
                        {visibleCols.inspectors && <th className="enquiry-table-mobile-hide">INSPECTOR(S)</th>}
                        {visibleCols.enquiryType && <th className="enquiry-table-mobile-hide">TYPE</th>}
                        <th>DOCS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0
                        ? <tr><td colSpan={(canSeeCoordinator ? 11 : 10) + visibleColCount} style={{ textAlign: 'center', padding: 40 }}>No enquiries found</td></tr>
                        : rows.map((r, i) => {
                          const noInspector = !r.inspectors?.length && r.status !== 'LOST';
                          const noneApproved = r.inspectors?.length > 0 && !r.inspectors.some(i => i.status === 'APPROVED') && r.status !== 'LOST';
                          const showRed = noInspector || noneApproved;
                          return (
                          <tr key={r._id || i}
                            className={selectedIds.includes(r._id) ? 'selected' : ''}
                            style={!selectedIds.includes(r._id) && showRed ? { backgroundColor: '#ffd8d8' } : {}}
                            onClick={() => toggleRow(r._id)}>
                            <td onClick={e => e.stopPropagation()}>
                              <input type="checkbox"
                                checked={selectedIds.includes(r._id)}
                                onChange={() => toggleRow(r._id)} />
                            </td>
                            <td>{r.jobNumber}</td>
                            <td>{formatDate(r.enquiryDate)}</td>
                            <td className="enquiry-table-mobile-hide">{lookupsLoading ? 'Loading...' : (customerLookup[r.customerId] || '—')}</td>
                            <td className="enquiry-table-mobile-hide">{r.tentativeDates ? formatDate(r.tentativeDates.split(',')[0].trim()) : '—'}</td>
                            <td className="enquiry-table-mobile-hide">{lookupsLoading ? 'Loading...' : (vendorLocationLookup[r.manufacturerVendor] || '—')}</td>
                            <td className="enquiry-table-mobile-hide" title={r.customerRequirement || ''}>
                              {r.customerRequirement
                                ? r.customerRequirement.length > 15
                                  ? r.customerRequirement.slice(0, 15) + '...'
                                  : r.customerRequirement
                                : '—'}
                            </td>
                            <td className="enquiry-table-mobile-hide">{r.remarkStatus || '—'}</td>
                            <td className="enquiry-table-mobile-hide" title={r.remark || ''}>
                              {r.remark
                                ? r.remark.length > 15
                                  ? r.remark.slice(0, 15) + '...'
                                  : r.remark
                                : '—'}
                            </td>
                            {canSeeCoordinator && (
                              <td className="enquiry-table-mobile-hide">{r.createdBy?.fullName || '—'}</td>
                            )}
                            {visibleCols.company && (
                              <td className="enquiry-table-mobile-hide">{lookupsLoading ? 'Loading...' : (companyLookup[r.company] || '—')}</td>
                            )}
                            {visibleCols.vendorName && (
                              <td className="enquiry-table-mobile-hide">{lookupsLoading ? 'Loading...' : (vendorNameLookup[r.manufacturerVendor] || '—')}</td>
                            )}
                            {visibleCols.country && <td className="enquiry-table-mobile-hide">{r.country || '—'}</td>}
                            {visibleCols.totalDays && <td className="enquiry-table-mobile-hide">{r.totalDays || 0}</td>}
                            {visibleCols.coordinator && (
                              <td className="enquiry-table-mobile-hide">{r.customerCoordinator?.length ? r.customerCoordinator.join(', ') : '—'}</td>
                            )}
                            {visibleCols.products && (
                              <td className="enquiry-table-mobile-hide">
                                {r.selectedProducts?.length
                                  ? r.selectedProducts.map(pid => productLookup[pid] || pid).join(', ')
                                  : '—'}
                              </td>
                            )}
                            {visibleCols.inspectors && (() => {
                              // Cap at 15 chars + "..." — full list stays available on hover
                              const names = r.inspectors?.map(insp => insp.inspectorName).filter(Boolean).join(', ') || '';
                              return (
                                <td className="enquiry-table-mobile-hide" title={names.length > 15 ? names : undefined}>
                                  {!names ? '—' : names.length > 15 ? `${names.slice(0, 15)}...` : names}
                                </td>
                              );
                            })()}
                            {visibleCols.enquiryType && (
                              <td className="enquiry-table-mobile-hide">{r.enquiryType === 'MANMONTH' ? 'Manmonth' : 'Manday'}</td>
                            )}
                            <td style={{ textAlign: 'center' }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                {/* Invoice */}
                                {r.invoices?.length > 0
                                  ? <span title={`${r.invoices.length} invoice(s)`} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#1a3a6b', fontSize: 12 }}>
                                      <FaFileInvoiceDollar size={15} />{r.invoices.length}
                                    </span>
                                  : <IoWarningOutline size={14} color="#e74c3c" title="No invoice uploaded" />}
                                {/* Work Order */}
                                {r.inspectors?.some(i => i.workOrderUrl)
                                  ? <IoNewspaperSharp size={16} color="#0078d4" title="Work Order uploaded" />
                                  : <IoWarningOutline size={14} color="#e74c3c" title="No work order uploaded" />}
                                {/* Deliverables */}
                                {r.deliverables?.some(d => d.fileUrl)
                                  ? <FaFolderOpen size={14} color="#e67e22" title="Documents uploaded" />
                                  : <IoWarningOutline size={14} color="#e74c3c" title="No documents uploaded" />}
                                {/* Client Invoice (Finance Sales Invoice) */}
                                {r.financeInvoice
                                  ? <FaMoneyCheckDollar size={14} color="#27ae60" title={`Client invoice created: ${r.financeInvoice.invoiceNumber || '—'} (${r.financeInvoice.status})`} />
                                  : <IoWarningOutline size={14} color="#e74c3c" title="No client invoice yet" />}
                              </span>
                            </td>
                          </tr>
                          );
                        })
                      }
                    </tbody>
                  </table>

                  {/* Pagination */}
                  {total > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, padding: '8px 16px', background: '#f8fafc', borderTop: '1px solid #eee' }}>
                      <select
                        value={limit}
                        onChange={e => setLimit(Number(e.target.value))}
                        style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: 4, fontSize: 12, cursor: 'pointer', height: 32 }}
                      >
                        {[25, 50, 75, 100].map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                      <span style={{ fontSize: 12, color: '#888', marginRight: 'auto' }}>Page {page} of {Math.max(1, pages)}</span>
                      <button
                        disabled={page <= 1}
                        onClick={() => fetchRows(page - 1)}
                        style={{ display: 'inline-flex', alignItems: 'center', height: 32, padding: '0 14px', border: '1px solid #ddd', borderRadius: 4, background: '#fff', fontSize: 12, cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1 }}
                      >Prev</button>
                      <button
                        disabled={page >= pages}
                        onClick={() => fetchRows(page + 1)}
                        style={{ display: 'inline-flex', alignItems: 'center', height: 32, padding: '0 14px', border: '1px solid #ddd', borderRadius: 4, background: '#fff', fontSize: 12, cursor: page >= pages ? 'not-allowed' : 'pointer', opacity: page >= pages ? 0.5 : 1 }}
                      >Next</button>
                    </div>
                  )}
                </>
              )
            }
          </div>
        )}
      </div>

      {/* ── Send-to-Finance date selection popup ── */}
      {showSendModal && (
        <div
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => !sendingToFinance && setShowSendModal(false)}
        >
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', maxWidth: '560px', width: '90%', maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,.18)' }}>
            <div style={{ background: '#1a2a4d', color: '#fff', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontWeight: 600, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}><FaFileInvoiceDollar /> SEND TO FINANCE — SELECT INSPECTION DATES</h3>
              <button onClick={() => !sendingToFinance && setShowSendModal(false)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, display: 'flex', cursor: 'pointer' }}><FiX /></button>
            </div>
            <div style={{ padding: '20px', overflowY: 'auto' }}>
            <p style={{ fontSize: 12, color: '#777', margin: '0 0 14px' }}>
              Only dates with an uploaded report can be selected. Dates already invoiced are locked.
            </p>
            {sendInspectorDates.map((ins, idx) => {
              const isLegacy = (ins.dates || []).length === 0;
              const reported = new Set(ins.reportedDates || []);
              const invoiced = new Set(ins.invoicedDates || []);
              const picked = pickedDates[idx] || [];
              return (
                <div key={idx} style={{ marginBottom: 14, paddingBottom: 12, borderBottom: '1px solid #eee' }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: '#222' }}>{ins.inspectorName || '—'}</div>
                  {isLegacy ? (
                    ins.hasInvoice ? (
                      <span style={{ fontSize: 12, background: '#27ae60', color: '#fff', padding: '4px 10px', borderRadius: 4 }} title="Invoice already created">
                        ✓ Invoiced — {ins.numberOfDays} day(s)
                      </span>
                    ) : reported.size > 0 ? (
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}>
                        <input type="checkbox" checked={picked.includes('*')} onChange={() => toggleSendDate(idx, '*')} />
                        {ins.numberOfDays} day(s) — no date list
                      </label>
                    ) : (
                      <span style={{ fontSize: 12, color: '#999' }} title="No report uploaded for this inspector">
                        {ins.numberOfDays} day(s) — no report uploaded
                      </span>
                    )
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {(ins.dates || []).map(d => {
                        if (invoiced.has(d)) {
                          return (
                            <span key={d} title="Invoice already created for this date" style={{ fontSize: 12, background: '#27ae60', color: '#fff', padding: '4px 10px', borderRadius: 4, cursor: 'not-allowed' }}>
                              ✓ {formatDate(d)}
                            </span>
                          );
                        }
                        if (!reported.has(d)) {
                          return (
                            <span key={d} title="No report uploaded for this date" style={{ fontSize: 12, background: '#f0f0f0', color: '#aaa', padding: '4px 10px', borderRadius: 4, cursor: 'not-allowed' }}>
                              {formatDate(d)}
                            </span>
                          );
                        }
                        const isPicked = picked.includes(d);
                        return (
                          <label key={d} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, padding: '4px 10px', borderRadius: 4, cursor: 'pointer', border: `1px solid ${isPicked ? '#437cf0' : '#ddd'}`, background: isPicked ? '#eaf3ff' : '#fff', color: '#333' }}>
                            <input type="checkbox" checked={isPicked} onChange={() => toggleSendDate(idx, d)} style={{ margin: 0 }} />
                            {formatDate(d)}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
              <button type="button" style={{ background: '#f0f0f0', padding: '10px 20px', border: 'none', borderRadius: '4px', cursor: sendingToFinance ? 'not-allowed' : 'pointer' }} disabled={sendingToFinance} onClick={() => setShowSendModal(false)}>Cancel</button>
              <button
                type="button"
                style={{ background: '#1a2a4d', color: '#fff', padding: '10px 20px', border: 'none', borderRadius: '4px', cursor: pickedCount === 0 || sendingToFinance ? 'not-allowed' : 'pointer', opacity: pickedCount === 0 || sendingToFinance ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 6 }}
                disabled={pickedCount === 0 || sendingToFinance}
                onClick={confirmSendToFinance}
              >
                <FaFileInvoiceDollar /> {sendingToFinance ? 'Sending…' : `Send to Finance (${pickedCount})`}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}
    </div >
  );
};

export default Enquiry;